// Teams Service - PowerShell-based Teams federation management
// Connects to backend Express API that uses Teams PowerShell cmdlets for actual federation control

import { apiClient, ApiResponse } from './apiClient';

// Teams federation configuration interfaces
export interface TeamsFederationConfig {
  allowFederatedUsers: boolean;
  allowPublicUsers: boolean;
  allowTeamsConsumer: boolean;
  allowedDomains?: string[];
  blockedDomains?: string[];
  allowedDomainsCount: number;
  blockedDomainsCount: number;
}

export interface TeamsHealthStatus {
  connected: boolean;
  tenantInfo?: {
    tenantId: string;
    clientId: string;
  };
  moduleVersion?: string;
  error?: string;
  keyVault?: {
    connected: boolean;
    url: string;
  };
  powershell?: {
    connected: boolean;
    version?: string;
  };
}

export interface TeamsDomainAction {
  domain: string;
  action: 'allowed' | 'blocked' | 'removed';
  result: string;
  reason?: string;
}

export interface TeamsPolicy {
  identity: string;
  displayName: string;
  description?: string;
  allowFederatedUsers: boolean;
  allowPublicUsers: boolean;
  allowTeamsConsumer: boolean;
  allowedDomains: string[];
  blockedDomains: string[];
  inherited_domains?: string[];  // Domains inherited from parent policy (for managed policies ending in _OD)
  createdDate: string;
  lastModified: string;
  syncedAt?: string;
  source: string;
  status?: string;
  removedAt?: string;
  // Extended attributes
  enableOutsideAccess?: boolean;
  enablePublicCloudAccess?: boolean;
  enablePublicCloudAudioVideoAccess?: boolean;
  enableTeamsConsumerInbound?: boolean;
  enableTeamsSMS?: boolean;
  enableXMPPAccess?: boolean;
  restrictTeamsConsumerToExternalUserProfiles?: boolean;
  communicationWithExternalOrgs?: string;
}

export interface TeamsPolicySyncResult {
  success: boolean;
  message: string;
  status: string;
  timestamp: string;
  policiesProcessed: number;
  policiesAdded: number;
  policiesUpdated: number;
  policiesRemoved: number;
  changes?: {
    added: string[];
    updated: string[];
    removed: string[];
  };
  errors: string[];
}

export interface TeamsDomainOperationRequest {
  domain: string;
  reason?: string;
}

export interface TeamsDomainOperationResponse {
  success: boolean;
  message: string;
  data: TeamsDomainAction;
  timestamp: string;
}

class TeamsService {
  // Health check - test Teams Graph API connectivity
  async checkHealth(): Promise<TeamsHealthStatus> {
    try {
      const response: any = await apiClient.get<any>('/teams/health');

      // The response structure directly contains the fields
      return {
        ...response.services.teams,
        keyVault: response.services.keyVault,
        tenantInfo: {
          tenantId: response.environment?.tenantId || response.services.teams.tenantInfo?.tenantId,
          clientId: response.services.teams.tenantInfo?.clientId,
          moduleAvailable: response.services.teams.tenantInfo?.displayName === 'Service Principal',
          importResult: response.services.teams.connected ? 'success' : 'failed'
        }
      };
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to check Teams connectivity. Please try again.');
    }
  }

  // Check Teams connection status (alias for checkHealth)
  async checkConnection(): Promise<TeamsHealthStatus> {
    return this.checkHealth();
  }

  // Get current Teams federation configuration
  async getFederationConfig(): Promise<TeamsFederationConfig> {
    try {
      const response = await apiClient.get<any>('/teams/federation/config');

      // Check if response has data wrapper or direct fields
      const data = response.data || response;

      return {
        allowFederatedUsers: data.settings?.allowFederatedUsers || data.federation?.allowFederatedUsers,
        allowPublicUsers: data.settings?.allowPublicUsers || data.federation?.allowPublicUsers,
        allowTeamsConsumer: data.settings?.allowTeamsConsumer || data.federation?.allowTeamsConsumer,
        allowedDomains: data.federation?.allowedDomains || [],
        blockedDomains: data.federation?.blockedDomains || [],
        allowedDomainsCount: data.allowedDomainsCount || 0,
        blockedDomainsCount: data.blockedDomainsCount || 0
      };
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to load Teams federation configuration. Please try again.');
    }
  }

  // Add domain to allowed federation list
  async allowDomain(request: TeamsDomainOperationRequest): Promise<TeamsDomainOperationResponse> {
    try {
      const response = await apiClient.post<TeamsDomainAction>('/teams/federation/domains/allow', request);

      return {
        success: true,
        message: `Domain '${request.domain}' added to allowed federation list`,
        data: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      // Silently handle errors
      throw new Error(`Failed to add '${request.domain}' to allowed list. Please check your permissions and try again.`);
    }
  }

  // Remove domain from allowed federation list
  async removeAllowedDomain(domain: string, reason?: string): Promise<TeamsDomainOperationResponse> {
    try {
      const response = await apiClient.delete<TeamsDomainAction>(`/teams/federation/domains/allow/${encodeURIComponent(domain)}`);

      return {
        success: true,
        message: `Domain '${domain}' removed from allowed federation list`,
        data: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      // Silently handle errors
      throw new Error(`Failed to remove '${domain}' from allowed list. Please try again.`);
    }
  }

  // Add domain to blocked federation list
  async blockDomain(request: TeamsDomainOperationRequest): Promise<TeamsDomainOperationResponse> {
    try {
      const response = await apiClient.post<TeamsDomainAction>('/teams/federation/domains/block', request);

      return {
        success: true,
        message: `Domain '${request.domain}' added to blocked federation list`,
        data: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      // Silently handle errors
      throw new Error(`Failed to add '${request.domain}' to blocked list. Please check your permissions and try again.`);
    }
  }

  // Get Teams policies
  async getPolicies(): Promise<TeamsPolicy[]> {
    try {
      const response = await apiClient.get<{
        policies: TeamsPolicy[];
        count: number;
      }>('/policies');

      return response.data.policies;
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to load Teams policies. Please try again.');
    }
  }

  // Manually trigger Teams authentication
  async connect(): Promise<void> {
    try {
      await apiClient.post('/teams/connect');
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to connect to Teams. Please check your service principal configuration.');
    }
  }

  // Disconnect from Teams
  async disconnect(): Promise<void> {
    try {
      await apiClient.post('/teams/disconnect');
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to disconnect from Teams.');
    }
  }

  // Bulk domain operations
  async bulkAllowDomains(domains: string[], reason?: string): Promise<TeamsDomainOperationResponse[]> {
    const results: TeamsDomainOperationResponse[] = [];

    for (const domain of domains) {
      try {
        const result = await this.allowDomain({ domain, reason });
        results.push(result);
      } catch (error) {
        results.push({
          success: false,
          message: error instanceof Error ? error.message : 'Unknown error',
          data: {
            domain,
            action: 'allowed',
            result: 'failed',
            reason
          },
          timestamp: new Date().toISOString()
        });
      }
    }

    return results;
  }

  async bulkBlockDomains(domains: string[], reason?: string): Promise<TeamsDomainOperationResponse[]> {
    const results: TeamsDomainOperationResponse[] = [];

    for (const domain of domains) {
      try {
        const result = await this.blockDomain({ domain, reason });
        results.push(result);
      } catch (error) {
        results.push({
          success: false,
          message: error instanceof Error ? error.message : 'Unknown error',
          data: {
            domain,
            action: 'blocked',
            result: 'failed',
            reason
          },
          timestamp: new Date().toISOString()
        });
      }
    }

    return results;
  }

  // Validate domain format
  static validateDomain(domain: string): boolean {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;
    return domainRegex.test(domain);
  }

  // Get federation status summary for dashboard
  async getFederationSummary(): Promise<{
    isHealthy: boolean;
    allowedDomainsCount: number;
    blockedDomainsCount: number;
    federationEnabled: boolean;
    lastChecked: string;
  }> {
    try {
      const [health, config] = await Promise.all([
        this.checkHealth(),
        this.getFederationConfig()
      ]);

      return {
        isHealthy: health.connected,
        allowedDomainsCount: config.allowedDomainsCount,
        blockedDomainsCount: config.blockedDomainsCount,
        federationEnabled: config.allowFederatedUsers,
        lastChecked: new Date().toISOString()
      };
    } catch (error) {
      // Silently handle errors
      return {
        isHealthy: false,
        allowedDomainsCount: 0,
        blockedDomainsCount: 0,
        federationEnabled: false,
        lastChecked: new Date().toISOString()
      };
    }
  }

  // Get Teams policies with sync data from PostgreSQL
  async getTeamsPolicies(): Promise<TeamsPolicy[]> {
    try {
      console.log('🔄 Fetching Teams policies...');
      const response = await apiClient.get<any>('/teams/policies');

      // Handle both wrapped and direct response formats
      const policiesData = (response as any).policies || response.data || response;

      // Normalize each policy to match frontend expectations
      const policies = Array.isArray(policiesData) ? policiesData.map((policy: any) => ({
        ...policy,
        identity: policy.identity || policy.Identity,
        displayName: policy.displayName || policy.DisplayName || policy.Identity,
        allowedDomains: policy.allowedDomains || policy.AllowedExternalDomains || [],
        blockedDomains: policy.blockedDomains || policy.BlockedExternalDomains || [],
        allowFederatedUsers: policy.allowFederatedUsers ?? policy.EnableFederationAccess ?? false,
        allowPublicUsers: policy.allowPublicUsers ?? policy.EnablePublicCloudAccess ?? false,
        allowTeamsConsumer: policy.allowTeamsConsumer ?? policy.EnableTeamsConsumerAccess ?? false,
        description: policy.description || policy.Description || '',
        createdDate: policy.createdDate || new Date().toISOString(),
        lastModified: policy.lastModified || new Date().toISOString(),
        source: policy.source || 'Teams'
      })) : [];

      console.log(`✅ Fetched ${policies.length} Teams policies`);
      return policies;
    } catch (error) {
      console.error('❌ Failed to fetch Teams policies:', error);
      throw error;
    }
  }

  // Sync Policies policies from PowerShell
  async syncTeamsPolicies(forceRefresh: boolean = false): Promise<TeamsPolicySyncResult> {
    try {
      console.log('🔄 Syncing Teams policies from PowerShell...');
      const response = await apiClient.post<TeamsPolicySyncResult>('/teams/policies/sync', {
        forceRefresh
      });

      // The response structure is: { success: boolean, data: TeamsPolicySyncResult }
      // The apiClient returns ApiResponse<T> where T is TeamsPolicySyncResult
      // We need to add the success flag from the wrapper to the result
      const result = {
        ...response.data,
        success: response.success
      };

      console.log('✅ Teams policies sync completed:', result);
      return result;
    } catch (error) {
      console.error('❌ Failed to sync Teams policies:', error);
      // Return a structured error response instead of throwing
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync Teams policies',
        status: 'failed',
        timestamp: new Date().toISOString(),
        policiesProcessed: 0,
        policiesAdded: 0,
        policiesUpdated: 0,
        policiesRemoved: 0,
        errors: [error instanceof Error ? error.message : 'Failed to sync Teams policies']
      };
    }
  }

  // Field mapping configurations for normalization
  private static readonly FIELD_MAPPINGS = {
    domains: {
      allowedDomains: ['allowedDomains', 'AllowedExternalDomains'],
      blockedDomains: ['blockedDomains', 'BlockedExternalDomains']
    },
    identity: {
      identity: ['identity', 'Identity'],
      displayName: ['displayName', 'DisplayName', 'Identity'],
      description: ['description', 'Description']
    },
    booleanFields: {
      allowFederatedUsers: ['allowFederatedUsers', 'EnableFederationAccess'],
      allowPublicUsers: ['allowPublicUsers', 'EnablePublicCloudAccess'],
      allowTeamsConsumer: ['allowTeamsConsumer', 'EnableTeamsConsumerAccess']
    },
    groupFields: {
      displayName: ['assignedGroupDisplayName', 'AssignedGroupDisplayName', 'userGroup'],
      groupId: ['groupId', 'assignedGroupId', 'AssignedGroupId']
    }
  };

  // Helper method to resolve field value from multiple potential sources
  private resolveFieldValue(policyData: any, sourcePaths: string[], defaultValue: any = ''): any {
    for (const path of sourcePaths) {
      if (policyData[path] !== undefined && policyData[path] !== null) {
        return policyData[path];
      }
    }
    return defaultValue;
  }

  // Extract domain-related fields
  private mapDomainFields(policyData: any): any {
    const { domains } = TeamsService.FIELD_MAPPINGS;
    return {
      allowedDomains: this.resolveFieldValue(policyData, domains.allowedDomains, []),
      blockedDomains: this.resolveFieldValue(policyData, domains.blockedDomains, []),
      inherited_domains: policyData.inherited_domains || []
    };
  }

  // Extract identity-related fields
  private mapIdentityFields(policyData: any): any {
    const { identity } = TeamsService.FIELD_MAPPINGS;
    const resolvedIdentity = this.resolveFieldValue(policyData, identity.identity);
    return {
      identity: resolvedIdentity,
      displayName: this.resolveFieldValue(policyData, identity.displayName, resolvedIdentity),
      description: this.resolveFieldValue(policyData, identity.description)
    };
  }

  // Extract boolean permission fields
  private mapBooleanFields(policyData: any): any {
    const { booleanFields } = TeamsService.FIELD_MAPPINGS;
    return {
      allowFederatedUsers: this.resolveFieldValue(policyData, booleanFields.allowFederatedUsers, false),
      allowPublicUsers: this.resolveFieldValue(policyData, booleanFields.allowPublicUsers, false),
      allowTeamsConsumer: this.resolveFieldValue(policyData, booleanFields.allowTeamsConsumer, false)
    };
  }

  // Extract group-related fields
  private mapGroupFields(policyData: any): any {
    const { groupFields } = TeamsService.FIELD_MAPPINGS;
    const groupDisplayName = this.resolveFieldValue(policyData, groupFields.displayName);
    return {
      assignedGroupDisplayName: groupDisplayName,
      groupDisplayName: groupDisplayName,
      groupId: this.resolveFieldValue(policyData, groupFields.groupId)
    };
  }

  // Add metadata fields with defaults
  private addMetadataFields(policyData: any): any {
    return {
      createdDate: policyData.createdDate || new Date().toISOString(),
      lastModified: policyData.lastModified || new Date().toISOString(),
      source: policyData.source || 'Teams',
      assignments: policyData.assignments || { groups: [], users: [], totalAssignments: 0 }
    };
  }

  logUKStaffPolicy = (normalizedPolicy: any) => {
    if (normalizedPolicy.identity === 'UK Staff' || normalizedPolicy.identity === 'UK Staff_OD') {
      console.error('🚨 UK STAFF NORMALIZED POLICY:', {
        name: normalizedPolicy.identity,
        allowedDomains: normalizedPolicy.allowedDomains,
        blockedDomains: normalizedPolicy.blockedDomains,
        allowedCount: normalizedPolicy.allowedDomains?.length || 0,
        blockedCount: normalizedPolicy.blockedDomains?.length || 0
      });
    }
  }

  logNormalizedPolicy = (normalizedPolicy: any) => {
    console.log(`🔍 [NORMALIZED] Policy ${normalizedPolicy.identity} assignments:`, {
      hasAssignments: !!normalizedPolicy.assignments,
      groupsCount: normalizedPolicy.assignments?.groups?.length || 0,
      firstGroupRank: normalizedPolicy.assignments?.groups?.[0]?.rank,
      fullAssignments: normalizedPolicy.assignments
    });
  }

  getNormalizedPolicy = (policyData: any): any => {
    // CRITICAL FIX: Normalize field names from backend
    // Backend returns AllowedExternalDomains (capital case)
    // Frontend expects allowedDomains (camelCase)
    const normalizedPolicy = {
      ...policyData,
      ...this.mapDomainFields(policyData),
      ...this.mapIdentityFields(policyData),
      ...this.mapBooleanFields(policyData),
      ...this.mapGroupFields(policyData),
      ...this.addMetadataFields(policyData)
    };

    this.logNormalizedPolicy(normalizedPolicy);
    this.logUKStaffPolicy(normalizedPolicy); // CRITICAL: Log UK Staff domains specifically

    return normalizedPolicy;
  }

  // Get individual Teams policy by name
  async getTeamsPolicy(policyName: string): Promise<TeamsPolicy> {
    try {
      console.log(`🔄 Fetching Teams policy: ${policyName}`);
      const response = await apiClient.get<TeamsPolicy>(`/policies/${encodeURIComponent(policyName)}`);
      console.log(`✅ Fetched Teams policy: ${policyName}`, response);
      console.log(`🔍 [ASSIGNMENTS DEBUG] Response structure:`, {
        hasData: !!(response as any).data,
        hasAssignments: !!((response as any).data?.assignments || (response as any).assignments),
        assignments: (response as any).data?.assignments || (response as any).assignments
      });

      // The backend returns { success: true, data: policy }
      // We need to extract the actual policy data
      const policyData = (response as any).data || response;

      console.log('🚨🚨🚨 RAW API RESPONSE policyData:', JSON.stringify(policyData, null, 2));
      console.log('🚨🚨🚨 RAW API RESPONSE policyData.assignments:', JSON.stringify(policyData.assignments, null, 2));

      return this.getNormalizedPolicy(policyData);
    } catch (error) {
      console.error(`❌ Failed to fetch Teams policy ${policyName}:`, error);
      throw error;
    }
  }

  // Async policy deletion - Queue policy deletion (database-first, PowerShell async)
  async deletePolicyAsync(policyIdOrName: string): Promise<{
    success: boolean;
    message: string;
    deletionId: string;
    policyName: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    requestedAt: string;
    requestedBy: string;
  }> {
    try {
      console.log(`🗑️ Queuing async deletion for policy: ${policyIdOrName}`);
      const response = await apiClient.delete<any>(`/policies/${encodeURIComponent(policyIdOrName)}`);
      console.log(`✅ Policy deletion queued:`, response);

      return {
        success: response.success,
        message: response.message || 'Policy deletion queued',
        deletionId: response.data.deletionId,
        policyName: response.data.policyName,
        status: response.data.status,
        requestedAt: response.data.requestedAt,
        requestedBy: response.data.requestedBy
      };
    } catch (error) {
      console.error(`❌ Failed to queue policy deletion ${policyIdOrName}:`, error);
      throw error;
    }
  }

  // Get policy deletion status
  async getPolicyDeletionStatus(policyIdOrName: string): Promise<{
    hasDeletion: boolean;
    data: {
      id: string;
      policy_name: string;
      status: 'pending' | 'in_progress' | 'completed' | 'failed';
      requested_at: string;
      started_at?: string;
      completed_at?: string;
      error_message?: string;
      retry_count: number;
      deletion_steps_completed: {
        remove_user_assignments: boolean;
        remove_group_assignments: boolean;
        delete_from_teams: boolean;
        verify_deletion: boolean;
      };
    } | null;
  }> {
    try {
      console.log(`🔍 Checking deletion status for policy: ${policyIdOrName}`);
      const response = await apiClient.get<any>(`/policies/${encodeURIComponent(policyIdOrName)}/deletion-status`);
      console.log(`✅ Deletion status:`, response);

      return {
        hasDeletion: response.data.hasDeletion || false,
        data: response.data.data || response.data || null
      };
    } catch (error) {
      console.error(`❌ Failed to get deletion status for ${policyIdOrName}:`, error);
      throw error;
    }
  }

  // Get all deletion jobs
  async getDeletionJobs(status?: 'pending' | 'in_progress' | 'completed' | 'failed', limit: number = 50): Promise<any[]> {
    try {
      console.log(`📋 Fetching deletion jobs (status: ${status || 'all'})`);
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      params.append('limit', limit.toString());

      const response = await apiClient.get<any>(`/policies/deletions?${params.toString()}`);
      console.log(`✅ Fetched ${response.data.length} deletion jobs`);

      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch deletion jobs:`, error);
      throw error;
    }
  }
}

export const teamsService = new TeamsService();
